import isEmpty from 'lodash/isEmpty';
import Vue from 'vue';
import { isJiraConnected, getAllJiraProjects } from '@/vuex/modules/voc-items/voc-items.utils';
import {
    createJiraIssue,
    getJiraServerInfo,
    loadDetailsForJiraIssue,
    loadAllJiraIssuesForVocItem,
    unlinkJiraIssue,
    linkJiraIssueToVocItem,
    getJiraFields,
    getJiraInstances,
} from '@/voc/api/jira.api';

const state = {
    fetching: false,
    error: null,
    serverInfo: {},
    jiraProjects: [],
    jiraIssues: {},
    fields: {},
    instances: null,
    promises: {
        loadServerInfo: null,
        loadJiraIssueDetailsForVocItem: null,
        loadInstances: null,
    },
};

const mutations = {
    setFetching (state, { isFetching }) {
        state.fetching = isFetching;
    },
    setError (state, { error }) {
        if (error) {
            let errorMessage = error;
            if (error.response?.data && typeof error.response?.data === 'string') {
                errorMessage = error.response.data.trim();
            } else if (error.message && typeof error.message === 'string') {
                errorMessage = error.message.trim();
            }
            state.error = errorMessage;
        } else {
            state.error = error;
        }
    },
    setServerInfo (state, { serverInfo }) {
        state.serverInfo = serverInfo;
    },
    setJiraProjects (state, { jiraProjects }) {
        state.jiraProjects = jiraProjects;
    },
    setJiraIssues (state, { jiraIssues }) {
        state.jiraIssues = jiraIssues;
    },
    setPromise (state, { promise, key }) {
        Vue.set(state.promises, key, promise);
    },
    setFields (state, { projectId, issueTypeId, fields }) {
        if (!state.fields[projectId]) {
            Vue.set(state.fields, projectId, {});
        }

        Vue.set(state.fields[projectId], issueTypeId, fields);
    },
    setInstances (state, { instances }) {
        state.instances = instances;
    },
    resetState (state) {
        state.fetching = false;
        state.error = null;
        state.serverInfo = {};
        state.jiraProjects = [];
        state.jiraIssues = {};
        state.fields = {};
        state.promises = {
            loadServerInfo: null,
            loadJiraIssueDetailsForVocItem: null,
            loadInstances: null,
        };
    },
};

const actions = {
    async getJiraProjects ({ commit, state }, { reFetch = false } = {}) {
        if (!isEmpty(state.jiraProjects) && !reFetch) {
            return;
        }
        try {
            commit('setFetching', { fetching: true });
            const jiraProjects = await getAllJiraProjects();
            commit('setJiraProjects', { jiraProjects });
        } catch (error) {
            commit('setError', { error });
            throw error;
        } finally {
            commit('setFetching', { fetching: false });
        }
    },
    async createJiraIssue ({ commit, state, dispatch, rootGetters }, { fields }) {
        try {
            await dispatch('loadServerInfo');
            const vocItemId = rootGetters['vocItems/bladeActiveItem']?.id;
            const { isPrimary, ...formFields } = fields;
            const { id: jiraIssueId } = await createJiraIssue({ fields: formFields, vocItemId, isPrimary });

            loadDetailsForJiraIssue({ vocItemId, jiraIssueId }).then((jiraIssue) => {
                commit('setJiraIssues', {
                    jiraIssues: {
                        ...state.jiraIssues,
                        [vocItemId]: [
                            ...(state.jiraIssues[vocItemId] || []),
                            {
                                ...jiraIssue,
                                ...(state.serverInfo.baseUrl && {
                                    url: `${state.serverInfo.baseUrl}/browse/${jiraIssue.key}`,
                                }),
                                ...(jiraIssue.fields?.parent && {
                                    parentUrl: `${state.serverInfo.baseUrl}/browse/${jiraIssue.fields.parent.key}`,
                                }),
                            },
                        ],
                    },
                });
            });

            return jiraIssueId;
        } catch (error) {
            commit('setError', { error });
            throw error;
        }
    },
    async loadJiraIssueDetailsForVocItem (
        { commit, state, dispatch },
        { reFetch = false, vocItemId } = {},
    ) {
        try {
            await dispatch('loadServerInfo');
        } catch (error) {
            commit('setError', { error });
            throw error;
        }

        if (state.promises.loadJiraIssueDetailsForVocItem?.[vocItemId]) {
            return state.promises.loadJiraIssueDetailsForVocItem?.[vocItemId];
        }

        if (state.jiraIssues[vocItemId] && !reFetch) {
            return Promise.resolve();
        }

        const foundJiraIds = [];

        const promise = loadAllJiraIssuesForVocItem(vocItemId)
            .then((response) => {
                const jiraIssues = response.issues.map((jiraIssue) => {
                    foundJiraIds.push(jiraIssue.id);

                    if (!jiraIssue.fields) {
                        return {
                            ...jiraIssue,
                            error: jiraIssue.error || { message: 'jiraIssueId not found' },
                        };
                    }

                    return {
                        ...jiraIssue,
                        ...(state.serverInfo.baseUrl && {
                            url: `${state.serverInfo.baseUrl}/browse/${jiraIssue.key}`,
                        }),
                        ...(jiraIssue.fields?.parent && {
                            parentUrl: `${state.serverInfo.baseUrl}/browse/${jiraIssue.fields.parent.key}`,
                        }),
                    };
                });

                commit('setJiraIssues', {
                    jiraIssues: {
                        ...state.jiraIssues,
                        [vocItemId]: jiraIssues,
                    },
                });
            })
            .catch((error) => {
                commit('setError', { error });
                commit('setJiraIssues', {
                    jiraIssues: {
                        ...state.jiraIssues,
                        [vocItemId]: [],
                    },
                });

                return {
                    id: vocItemId,
                    error: {
                        message: error.response?.data || error.message,
                    },
                };
            })
            .finally(() => {
                commit('setPromise', { promise: null, key: ['loadJiraIssueDetailsForVocItem', vocItemId] });
            });

        commit('setPromise', { promise, key: ['loadJiraIssueDetailsForVocItem', vocItemId] });

        return promise;
    },
    async loadServerInfo ({ commit, state }, { reFetch = false } = {}) {
        if (state.promises.loadServerInfo) {
            return state.promises.loadServerInfo;
        }

        if (!isEmpty(state.serverInfo) && !reFetch) {
            return Promise.resolve();
        }

        if (state.error && !isJiraConnected(state.error)) {
            throw new Error(state.error);
        }

        const promise = getJiraServerInfo()
            .then((serverInfo) => {
                commit('setServerInfo', { serverInfo });
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                commit('setPromise', { promise: null, key: 'loadServerInfo' });
            });

        commit('setPromise', { promise, key: 'loadServerInfo' });

        return promise;
    },
    unlinkJiraIssue ({ commit, state, rootState }, { vocItemId, jiraIssueId }) {
        return unlinkJiraIssue({ vocItemId, jiraIssueId })
            .then(() => {
                const jiraIssues = state.jiraIssues[vocItemId].filter((jiraIssue) => jiraIssue.id !== jiraIssueId);

                const bladeItem = rootState.vocItems.blade.activeItems[rootState.vocItems.map[vocItemId]?.type];
                const mapItem = rootState.vocItems.map[vocItemId];

                commit('setJiraIssues', {
                    jiraIssues: {
                        ...state.jiraIssues,
                        [vocItemId]: jiraIssues,
                    },
                });
                commit(
                    'vocItems/updateItem',
                    {
                        item: {
                            ...bladeItem,
                            ...mapItem,
                            jiraIssueIds: rootState.vocItems.map[vocItemId]?.jiraIssueIds?.filter(
                                (id) => id !== jiraIssueId,
                            ),
                        },
                    },
                    { root: true },
                );
            })
            .catch((error) => {
                commit('setError', { error });
                throw error;
            });
    },
    async linkJiraIssuesToVocItem ({ commit, state }, { vocItemId, jiraIssueIdsAndKeys }) {
        const finalIssues = await linkJiraIssueToVocItem({ vocItemId, issues: jiraIssueIdsAndKeys })
            .then((jiraIssues) => {
                return jiraIssues.map((jiraIssue) => {
                    return {
                        ...jiraIssue,
                        ...(state.serverInfo.baseUrl && {
                            url: `${state.serverInfo.baseUrl}/browse/${jiraIssue.key}`,
                        }),
                        ...(jiraIssue.fields?.parent && {
                            parentUrl: `${state.serverInfo.baseUrl}/browse/${jiraIssue.fields.parent.key}`,
                        }),
                    };
                });
            })
            .catch((error) => {
                commit('setError', { error });
                throw error;
            });

        commit('setJiraIssues', {
            jiraIssues: {
                ...state.jiraIssues,
                [vocItemId]: [...(state.jiraIssues[vocItemId] || []), ...finalIssues],
            },
        });

        return finalIssues;
    },
    getFields ({ state, commit }, { projectId, issueTypeId, reFetch = false }) {
        if (!isEmpty(state.fields?.[projectId]?.[issueTypeId]) && !reFetch) {
            return Promise.resolve();
        }

        return getJiraFields({ projectId, issueTypeId })
            .then(({ fields }) => {
                commit('setFields', { projectId, issueTypeId, fields });
            })
            .catch((error) => {
                commit('setError', { error });
                throw error;
            });
    },
    async getInstances ({ state, commit }, { reFetch = false } = {}) {
        if (state.promises.loadInstances) {
            return state.promises.loadInstances;
        }

        if (state.instances !== null && !reFetch) {
            return Promise.resolve();
        }

        if (state.error && !isJiraConnected(state.error)) {
            commit('setError', { error: null });
        }

        const promise = getJiraInstances()
            .then(({ instances }) => {
                commit('setInstances', { instances });
                let jiraStatus = 'connected-configured-enabled';
                const instance = instances?.[0];
                if (!instance?.IPaasSolutionInstanceId) {
                    jiraStatus = 'not-connected';
                    commit('setError', { error: { message: 'jira integration instance not found' } });
                } else if (!instance?.Configured) {
                    jiraStatus = 'not-configured';
                    commit('setError', { error: { message: 'jira integration not configured' } });
                } else if (!instance?.Enabled) {
                    jiraStatus = 'disabled';
                    commit('setError', { error: { message: 'jira integration not enabled' } });
                }

                const { visitor, account } = window.pendo.getSerializedMetadata();
                window.pendo.updateOptions({
                    visitor,
                    account: {
                        ...account,
                        listenJiraIntegrationStatus: jiraStatus,
                    },
                });

                return instances;
            })
            .catch((error) => {
                commit('setError', { error });
                throw error;
            })
            .finally(() => {
                commit('setPromise', { promise: null, key: 'loadInstances' });
            });

        commit('setPromise', { promise, key: 'loadInstances' });

        return promise;
    },
};

const getters = {
    shouldShowJiraIntegration: (_state, _getters, _rootState, rootGetters) => {
        const vocProductLine = rootGetters['productLines/productLineByType']('voc');

        return !!vocProductLine?.metadata?.integrations?.jira?.enabled;
    },
    jiraIssuesForVocItem: (state) => (vocItemId) => {
        return state.jiraIssues[vocItemId];
    },
    instance: (state) => state.instances?.[0] || {},
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
